import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Client, MeetingMinute, Project, Task } from '../../types/crm';
import { isMeetingMinutePosted, minuteStatusLabel } from '../../lib/meetingMinutesHelpers';
import {
  activeTasks,
  doneTasks,
  newestMeetingMinutes,
  newestProjects,
  SIDEBAR_LIST_PREVIEW,
  takePreview,
} from '../../utils/accountSidebarList';
import { projectDetailPath } from '../../utils/dashboardHelpers';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { taskDisplayStatus, taskStatusModifier } from '../../utils/taskHelpers';
import { Modal } from '../ui/Modal';

type ViewAllSection = 'projects' | 'tasks' | 'minutes';

interface AccountDetailRelatedListsProps {
  client: Client;
  projects: Project[];
  tasks: Task[];
  minutes: MeetingMinute[];
  canEdit: boolean;
  onOpenMinutes: (minuteId: string | null) => void;
  onAddProject: () => void;
  onEditProject: (p: Project) => void;
  onAddTask: () => void;
  onOpenTask: (t: Task) => void;
}

function ViewAllLink({ count, onClick }: { count: number; onClick: () => void }) {
  if (count <= 0) return null;
  return (
    <button type="button" className="account-side-view-all" onClick={onClick}>
      View all ({count})
    </button>
  );
}

export function AccountDetailRelatedLists({
  client,
  projects,
  tasks,
  minutes,
  canEdit,
  onOpenMinutes,
  onAddProject,
  onEditProject,
  onAddTask,
  onOpenTask,
}: AccountDetailRelatedListsProps) {
  const [viewAllSection, setViewAllSection] = useState<ViewAllSection | null>(null);

  const sortedProjects = useMemo(() => newestProjects(projects), [projects]);
  const sortedMinutes = useMemo(() => newestMeetingMinutes(minutes), [minutes]);
  const openTasks = useMemo(() => activeTasks(tasks), [tasks]);
  const completedTasks = useMemo(() => doneTasks(tasks), [tasks]);

  const previewProjects = takePreview(sortedProjects);
  const previewMinutes = takePreview(sortedMinutes);
  const previewTasks = takePreview(openTasks);

  const hiddenProjectCount = Math.max(0, sortedProjects.length - SIDEBAR_LIST_PREVIEW);
  const hiddenMinuteCount = Math.max(0, sortedMinutes.length - SIDEBAR_LIST_PREVIEW);
  const hiddenTaskCount =
    Math.max(0, openTasks.length - SIDEBAR_LIST_PREVIEW) + completedTasks.length;

  function projectHref(p: Project) {
    return projectDetailPath(p);
  }

  function renderProjectRow(p: Project, inModal = false) {
    const href = projectHref(p);
    return (
      <li key={p.id} className="account-side-list__row">
        <div className="account-side-list__body">
          <Link to={href} className="account-side-list__name account-side-link">
            {p.reference_no ?? '—'} · {p.title}
          </Link>
          <span className="account-side-muted">{p.status ?? 'Ongoing'}</span>
        </div>
        <div className="account-side-list__btns">
          <Link to={href} className="account-side-btn" onClick={() => inModal && setViewAllSection(null)}>
            Open
          </Link>
          {canEdit && (
            <button
              type="button"
              className="account-side-btn account-side-btn--muted"
              onClick={() => {
                if (inModal) setViewAllSection(null);
                onEditProject(p);
              }}
            >
              Edit
            </button>
          )}
        </div>
      </li>
    );
  }

  function renderTaskRow(t: Task, inModal = false) {
    const display = taskDisplayStatus(t);
    return (
      <li key={t.id} className="account-side-list__row account-side-list__row--task">
        <button
          type="button"
          className="account-side-list__body account-side-list__body--button"
          onClick={() => {
            if (inModal) setViewAllSection(null);
            onOpenTask(t);
          }}
        >
          <span className={`task-status task-status--compact ${taskStatusModifier(display)}`}>
            {display}
          </span>
          <span className="account-side-list__name">{t.title}</span>
          {t.due_date && (
            <span
              className={`account-side-muted${display === 'Due' ? ' account-side-task-due--overdue' : ''}`}
            >
              Due {formatSingaporeDateTime(t.due_date)}
            </span>
          )}
          {t.description?.trim() && (
            <span className="account-side-muted account-side-list__notes">{t.description.trim()}</span>
          )}
        </button>
        <button
          type="button"
          className="account-side-btn"
          onClick={() => {
            if (inModal) setViewAllSection(null);
            onOpenTask(t);
          }}
        >
          Open
        </button>
      </li>
    );
  }

  function renderMinuteRow(m: MeetingMinute) {
    return (
      <li key={m.id} className="account-side-list__row">
        <div className="account-side-list__body">
          <span
            className={`minutes-modal__entry-badge${isMeetingMinutePosted(m) ? ' minutes-modal__entry-badge--posted' : ''}`}
          >
            {minuteStatusLabel(m)}
          </span>
          <span className="account-side-list__name">{m.title}</span>
          <span className="account-side-muted">
            {formatSingaporeDateTime(m.meeting_date ?? m.created_at)}
          </span>
        </div>
        <div className="account-side-list__btns">
          <button
            type="button"
            className="account-side-btn"
            onClick={() => {
              setViewAllSection(null);
              onOpenMinutes(m.id);
            }}
          >
            Open
          </button>
        </div>
      </li>
    );
  }

  const viewAllTitle =
    viewAllSection === 'projects'
      ? `Projects · ${client.company_name}`
      : viewAllSection === 'tasks'
        ? `Tasks · ${client.company_name}`
        : viewAllSection === 'minutes'
          ? `Meeting minutes · ${client.company_name}`
          : '';

  return (
    <>
      <section className="account-side-card">
        <div className="account-side-card__head">
          <h2 className="account-side-card__title">Projects ({projects.length})</h2>
          {canEdit && (
            <button type="button" className="account-side-btn" onClick={onAddProject}>
              Add
            </button>
          )}
        </div>
        {projects.length === 0 ? (
          <p className="account-side-muted">None yet.</p>
        ) : (
          <>
            <ul className="account-side-list">{previewProjects.map((p) => renderProjectRow(p))}</ul>
            <ViewAllLink count={hiddenProjectCount} onClick={() => setViewAllSection('projects')} />
          </>
        )}
      </section>

      <section className="account-side-card">
        <div className="account-side-card__head">
          <h2 className="account-side-card__title">Tasks ({tasks.length})</h2>
          {canEdit && (
            <button type="button" className="account-side-btn" onClick={onAddTask}>
              Add
            </button>
          )}
        </div>
        {tasks.length === 0 ? (
          <p className="account-side-muted">None yet.</p>
        ) : openTasks.length === 0 && completedTasks.length > 0 ? (
          <>
            <p className="account-side-muted">No open tasks.</p>
            <ViewAllLink count={completedTasks.length} onClick={() => setViewAllSection('tasks')} />
          </>
        ) : (
          <>
            <ul className="account-side-list">{previewTasks.map((t) => renderTaskRow(t))}</ul>
            <ViewAllLink count={hiddenTaskCount} onClick={() => setViewAllSection('tasks')} />
          </>
        )}
      </section>

      <section className="account-side-card">
        <div className="account-side-card__head">
          <h2 className="account-side-card__title">Meeting minutes ({minutes.length})</h2>
          {canEdit && (
            <button
              type="button"
              className="account-side-btn"
              onClick={() => onOpenMinutes(null)}
            >
              Add
            </button>
          )}
        </div>
        {minutes.length === 0 ? (
          <p className="account-side-muted">None yet.</p>
        ) : (
          <>
            <ul className="account-side-list">{previewMinutes.map((m) => renderMinuteRow(m))}</ul>
            <ViewAllLink count={hiddenMinuteCount} onClick={() => setViewAllSection('minutes')} />
          </>
        )}
      </section>

      <Modal
        title={viewAllTitle}
        open={viewAllSection !== null}
        onClose={() => setViewAllSection(null)}
        wide
      >
        {viewAllSection === 'projects' && (
          <ul className="account-side-list account-side-list--modal">
            {sortedProjects.map((p) => renderProjectRow(p, true))}
          </ul>
        )}
        {viewAllSection === 'tasks' && (
          <div className="account-side-view-all-tasks">
            {openTasks.length > 0 && (
              <>
                <p className="account-side-view-all-tasks__label">Open &amp; in progress</p>
                <ul className="account-side-list account-side-list--modal">
                  {openTasks.map((t) => renderTaskRow(t, true))}
                </ul>
              </>
            )}
            {completedTasks.length > 0 && (
              <>
                <p className="account-side-view-all-tasks__label account-side-view-all-tasks__label--done">
                  Done
                </p>
                <ul className="account-side-list account-side-list--modal">
                  {completedTasks.map((t) => renderTaskRow(t, true))}
                </ul>
              </>
            )}
            {openTasks.length === 0 && completedTasks.length === 0 && (
              <p className="account-side-muted">No tasks.</p>
            )}
          </div>
        )}
        {viewAllSection === 'minutes' && (
          <ul className="account-side-list account-side-list--modal">
            {sortedMinutes.map((m) => renderMinuteRow(m))}
          </ul>
        )}
      </Modal>
    </>
  );
}
